import type { FixedRoute, FixedRouteSegment, FixedRouteStop, FixedRouteStopConfig } from '../../types/fixedRoutes';
import type { ResultsVehicleRow, SimulationActivity } from '../../types/simulation';
import type { SegmentRouteMetadata } from './resultsPathsService';

export const OPTIMISED_FIXED_ROUTE_LAYER_NAME = 'Optimised Fixed Route Network';

export interface SimulationResultsToFixedRoutesInput {
  runId: string;
  activities: SimulationActivity[];
  vehicles?: ResultsVehicleRow[];
  routeMetadataByShift?: Record<string, SegmentRouteMetadata[]>;
  routeGeometryByShift?: Record<string, [number, number][][]>;
}

export interface SimulationResultsToFixedRoutesResult {
  stops: FixedRouteStop[];
  routes: FixedRoute[];
  warnings: string[];
}

type StopAgg = {
  pickupCount: number;
  dropoffCount: number;
  pickedUpRiderIds: Set<string>;
  droppedOffRiderIds: Set<string>;
};

type NormalizedActivity = SimulationActivity & {
  _shiftId: string;
  _timeMs: number;
  _clockMinutes: number | null;
  _seq: number;
  _lat: number;
  _lng: number;
};
type ElcShiftBand = 'DAY' | 'LATE' | 'NIGHT';
type ShiftNaming = {
  stableName: string | null;
  fleetName: string | null;
  manifestName: string | null;
  shiftBand: ElcShiftBand | null;
  /**
   * The name/rego of the VEHICLE the simulator assigned to this shift (from
   * `vehicle_name` / `shift_name`). This is the route's own vehicle — the key we
   * match against the Ops vehicle list when attaching operations (NOT the route
   * name). Persisted onto `simulationImport.opsVehicleLabel`.
   */
  vehicleName: string | null;
};

const DEFAULT_SEGMENT_MINUTES = 5;
const IMPORT_ROUTE_COLOR_PALETTE = [
  '#E53935',
  '#1E88E5',
  '#43A047',
  '#FB8C00',
  '#8E24AA',
  '#00897B',
  '#F4511E',
  '#3949AB',
  '#7CB342',
  '#6D4C41',
  '#00ACC1',
  '#C0CA33',
];

function colorForImportedRoute(index: number): string {
  if (index < IMPORT_ROUTE_COLOR_PALETTE.length) {
    return IMPORT_ROUTE_COLOR_PALETTE[index];
  }
  const hue = Math.round((index * 137.508) % 360);
  const sat = index % 2 === 0 ? 78 : 64;
  const light = index % 3 === 0 ? 46 : 54;
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

function toFinite(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function shiftIdFor(activity: SimulationActivity): string {
  const rec = activity as Record<string, unknown>;
  const shift = rec.vehicle_shift_id ?? rec.vehicle_id ?? rec.shift_id;
  if (typeof shift === 'string' && shift.trim() !== '') return shift.trim();
  return 'unknown-shift';
}

function resolveTimeMs(activity: SimulationActivity): number {
  const rec = activity as Record<string, unknown>;
  const candidates = [
    rec.activity_start_time,
    rec.activity_end_time,
    rec.activity_start,
    rec.min_activity_start_time,
    rec.start_time,
    rec.time,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c) {
      const t = Date.parse(c);
      if (Number.isFinite(t)) return t;
    }
  }
  return Number.MAX_SAFE_INTEGER;
}

function resolveClockMinutes(activity: SimulationActivity): number | null {
  const rec = activity as Record<string, unknown>;
  const candidates = [
    rec.activity_start_time,
    rec.activity_end_time,
    rec.activity_start,
    rec.min_activity_start_time,
    rec.start_time,
    rec.time,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || candidate.trim() === '') continue;
    const match = candidate.match(/(?:T|\s)(\d{2}):(\d{2})(?::\d{2})?/);
    if (match) {
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (Number.isFinite(hours) && Number.isFinite(minutes)) {
        return hours * 60 + minutes;
      }
    }

    const parsed = Date.parse(candidate);
    if (Number.isFinite(parsed)) {
      const date = new Date(parsed);
      return date.getUTCHours() * 60 + date.getUTCMinutes();
    }
  }

  return null;
}

function resolveSequence(activity: SimulationActivity, fallback: number): number {
  const rec = activity as Record<string, unknown>;
  const candidates = [rec.activity_sequence, rec.stop_number, rec.stop_visit_id, rec.min_driver_activity_number];
  for (const c of candidates) {
    const n = toFinite(c);
    if (n != null) return n;
  }
  return fallback;
}

function resolveLatLng(activity: SimulationActivity): [number, number] | null {
  const rec = activity as Record<string, unknown>;
  const latCandidates = [rec.activity_latitude, rec.latitude, rec.lat];
  const lngCandidates = [rec.activity_longitude, rec.longitude, rec.lng];

  let lat: number | null = null;
  let lng: number | null = null;
  for (const c of latCandidates) {
    lat = toFinite(c);
    if (lat != null) break;
  }
  for (const c of lngCandidates) {
    lng = toFinite(c);
    if (lng != null) break;
  }
  if (lat == null || lng == null) return null;
  return [lat, lng];
}

function inferPickDrop(activity: SimulationActivity): 'pick' | 'drop' | null {
  const rec = activity as Record<string, unknown>;
  const activityType = String(rec.activity_type ?? rec.type ?? '').toUpperCase();
  if (activityType.includes('PICK') || activityType.includes('ORIGIN')) return 'pick';
  if (activityType.includes('DROP') || activityType.includes('DEST')) return 'drop';

  const pick = toFinite(rec.number_of_rides_picked_up);
  const drop = toFinite(rec.number_of_rides_dropped_off);
  if ((pick ?? 0) > 0 && (drop ?? 0) === 0) return 'pick';
  if ((drop ?? 0) > 0 && (pick ?? 0) === 0) return 'drop';
  return null;
}

function pushIds(set: Set<string>, value: unknown): void {
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (typeof item === 'string' && item.trim() !== '') {
        set.add(item.trim());
      } else if (item && typeof item === 'object') {
        const rec = item as Record<string, unknown>;
        const id = rec.user_id ?? rec.userId ?? rec.id ?? rec.external_user_reference_id;
        if (typeof id === 'string' && id.trim() !== '') set.add(id.trim());
      }
    });
  }
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/**
 * Reads the human-readable name for a stop from the data the simulator actually
 * echoes back.
 *
 * The Postcode Stop Assignment tool writes the consolidated bus-stop / hub name
 * (e.g. "Lake Road Health Centre") into the ride's `from_address` (pickup end)
 * or `to_address` (destination end) — see `buildSimulationTripsSheet` /
 * `tripsSheetRowToRideSchema` in PostcodeStopAssignmentPage. Unlike
 * `external_stop_reference_id` (which HTTM drops at ingestion and never echoes),
 * `from_address`/`to_address` are persisted on the ride and returned on every
 * `/results/{id}/activities` fetch, so the name survives reopen / reload with no
 * server-side change.
 *
 * Resolution order:
 *   1. `external_stop_reference_id` (activity-level then `trips[].ride`) — kept as
 *      the top preference in case a future HTTM version echoes it; currently
 *      always absent.
 *   2. The leg-appropriate echoed ride address: `from_address` for a PICK stop,
 *      `to_address` for a DROP stop. This is the working path today.
 *   3. The activity's own `stop_name` / `location_name`.
 *
 * Returns `undefined` when nothing usable is present so callers keep the
 * existing `Stop N` placeholder (e.g. depot / START rows with no ride).
 */
function resolveStopName(activity: SimulationActivity): string | undefined {
  const rec = activity as Record<string, unknown>;

  // 1. Explicit external_stop_reference_id (future-proof; not echoed today).
  const direct = nonEmptyString(rec.external_stop_reference_id);
  if (direct) return direct;
  const trips = Array.isArray(rec.trips) ? rec.trips : [];
  for (const trip of trips) {
    if (!trip || typeof trip !== 'object') continue;
    const ride = (trip as Record<string, unknown>).ride;
    if (ride && typeof ride === 'object') {
      const value = nonEmptyString((ride as Record<string, unknown>).external_stop_reference_id);
      if (value) return value;
    }
  }

  // 2. Leg-appropriate echoed ride address. A PICK stop is named by where riders
  // board (`from_address`); a DROP stop by where they alight (`to_address`). This
  // keeps a destination stop from inheriting its riders' pickup name.
  const activityLeg = inferPickDrop(activity);
  for (const trip of trips) {
    if (!trip || typeof trip !== 'object') continue;
    const tr = trip as Record<string, unknown>;
    const ride = tr.ride;
    if (!ride || typeof ride !== 'object') continue;
    const r = ride as Record<string, unknown>;
    const legType = String(tr.activity_type ?? '').toUpperCase();
    const legIsPick = legType.includes('PICK') || legType.includes('ORIGIN');
    const legIsDrop = legType.includes('DROP') || legType.includes('DEST');
    const isPick = legIsPick || (!legIsDrop && legType === '' && activityLeg === 'pick');
    const isDrop = legIsDrop || (!legIsPick && legType === '' && activityLeg === 'drop');
    const address = isPick
      ? nonEmptyString(r.from_address)
      : isDrop
        ? nonEmptyString(r.to_address)
        : nonEmptyString(r.from_address) ?? nonEmptyString(r.to_address);
    if (address) return address;
  }

  // 3. Activity-level labels, if the API ever provides them.
  return nonEmptyString(rec.stop_name) ?? nonEmptyString(rec.location_name);
}

function buildStopAgg(activity: SimulationActivity): StopAgg {
  const rec = activity as Record<string, unknown>;
  const picked = new Set<string>();
  const dropped = new Set<string>();
  pushIds(picked, rec.users_picked_up);
  pushIds(dropped, rec.users_dropped_off);

  const trips = Array.isArray(rec.trips) ? rec.trips : [];
  trips.forEach((t) => {
    if (!t || typeof t !== 'object') return;
    const tr = t as Record<string, unknown>;
    const ride = tr.ride && typeof tr.ride === 'object' ? (tr.ride as Record<string, unknown>) : null;
    const riderId = tr.rider_ride_id ?? ride?.ride_id ?? ride?.external_user_reference_id;
    const leg = String(tr.activity_type ?? '').toUpperCase();
    if (typeof riderId === 'string' && riderId.trim() !== '') {
      if (leg === 'PICK') picked.add(riderId.trim());
      if (leg === 'DROP') dropped.add(riderId.trim());
    }
  });

  return {
    pickupCount: Math.max(toFinite(rec.number_of_rides_picked_up) ?? 0, picked.size),
    dropoffCount: Math.max(toFinite(rec.number_of_rides_dropped_off) ?? 0, dropped.size),
    pickedUpRiderIds: picked,
    droppedOffRiderIds: dropped,
  };
}

function minutesBetween(aMs: number, bMs: number): number {
  const diff = Math.max(0, bMs - aMs);
  if (!Number.isFinite(diff) || diff === 0) return DEFAULT_SEGMENT_MINUTES;
  return Math.max(1, Math.round(diff / 60000));
}

function minutesToHHmm(minutesSinceMidnight: number): string {
  const normalized = ((Math.round(minutesSinceMidnight) % (24 * 60)) + (24 * 60)) % (24 * 60);
  const h = String(Math.floor(normalized / 60)).padStart(2, '0');
  const m = String(normalized % 60).padStart(2, '0');
  return `${h}:${m}`;
}

function addMinutesToHHmm(time: string, minutesToAdd: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = ((h * 60 + m + minutesToAdd) % (24 * 60) + (24 * 60)) % (24 * 60);
  const hh = String(Math.floor(total / 60)).padStart(2, '0');
  const mm = String(total % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function normalizedLabel(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const collapsed = value.trim().replace(/\s+/g, ' ');
  return collapsed ? collapsed : null;
}

function inferShiftBand(value: unknown): ElcShiftBand | null {
  const text = normalizedLabel(value)?.toUpperCase();
  if (!text) return null;
  if (/\bDAY\b/.test(text)) return 'DAY';
  if (/\bLATE\b/.test(text)) return 'LATE';
  if (/\bNIGHT\b/.test(text)) return 'NIGHT';
  return null;
}

function inferOrdinal(value: unknown): number | null {
  const text = normalizedLabel(value);
  if (!text) return null;
  const match = text.match(/\b(\d{1,3})\b/);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function isGenericShiftName(value: string, shiftId: string): boolean {
  const upper = value.toUpperCase();
  const normalizedShift = shiftId.trim().toUpperCase();
  if (!upper) return true;
  if (upper === normalizedShift) return true;
  if (/^SHIFT\b/.test(upper)) return true;
  return false;
}

function extractNamingCandidatesFromRecord(record: Record<string, unknown>): {
  shiftBand: ElcShiftBand | null;
  routeName: string | null;
  fleetName: string | null;
  manifestName: string | null;
  shiftName: string | null;
  vehicleName: string | null;
} {
  const shiftBand = inferShiftBand(record.shift_band ?? record.shiftBand);
  return {
    shiftBand,
    routeName: normalizedLabel(record.route_name ?? record.routeName),
    fleetName: normalizedLabel(record.fleet_name ?? record.fleetName),
    manifestName: normalizedLabel(record.manifest_name ?? record.manifestName),
    shiftName: normalizedLabel(record.shift_name ?? record.shiftName),
    vehicleName: normalizedLabel(record.vehicle_name ?? record.vehicleName),
  };
}

function resolveShiftNaming(
  shiftId: string,
  activities: NormalizedActivity[],
  routeMetadata: SegmentRouteMetadata[] | undefined
): ShiftNaming {
  let shiftBand: ElcShiftBand | null = null;
  let routeName: string | null = null;
  let fleetName: string | null = null;
  let manifestName: string | null = null;
  let shiftName: string | null = null;
  let vehicleName: string | null = null;
  let ordinal: number | null = null;

  const visitRecord = (record: Record<string, unknown>) => {
    const extracted = extractNamingCandidatesFromRecord(record);
    shiftBand = shiftBand ?? extracted.shiftBand ?? inferShiftBand(record.shift_id ?? record.vehicle_shift_id ?? record.vehicle_id);
    routeName = routeName ?? extracted.routeName;
    fleetName = fleetName ?? extracted.fleetName;
    manifestName = manifestName ?? extracted.manifestName;
    shiftName = shiftName ?? extracted.shiftName;
    vehicleName = vehicleName ?? extracted.vehicleName;
    ordinal =
      ordinal ??
      inferOrdinal(extracted.routeName) ??
      inferOrdinal(extracted.fleetName) ??
      inferOrdinal(extracted.manifestName) ??
      inferOrdinal(extracted.shiftName) ??
      inferOrdinal(extracted.vehicleName);
  };

  routeMetadata?.forEach((row) => visitRecord(row as unknown as Record<string, unknown>));
  activities.forEach((activity) => {
    const record = activity as unknown as Record<string, unknown>;
    visitRecord(record);
    const trips = Array.isArray(record.trips) ? (record.trips as Array<Record<string, unknown>>) : [];
    trips.forEach((trip) => {
      visitRecord(trip);
      const ride = trip.ride;
      if (ride && typeof ride === 'object') {
        visitRecord(ride as Record<string, unknown>);
      }
    });
  });

  shiftBand = shiftBand ?? inferShiftBand(shiftId);
  ordinal = ordinal ?? inferOrdinal(shiftId);
  const derivedFleet = shiftBand && ordinal ? `${shiftBand} ${ordinal} COACH` : null;
  const derivedRoute = shiftBand && ordinal ? `${shiftBand} ${ordinal}` : null;

  const stableNameCandidate =
    routeName ??
    shiftName ??
    manifestName ??
    fleetName ??
    vehicleName ??
    derivedRoute ??
    derivedFleet;
  const stableName =
    stableNameCandidate && !isGenericShiftName(stableNameCandidate, shiftId)
      ? stableNameCandidate
      : derivedRoute ?? null;
  const finalFleetName =
    (fleetName && !isGenericShiftName(fleetName, shiftId) ? fleetName : null) ??
    (manifestName && !isGenericShiftName(manifestName, shiftId) ? manifestName : null) ??
    derivedFleet;
  const finalManifestName =
    (manifestName && !isGenericShiftName(manifestName, shiftId) ? manifestName : null) ??
    finalFleetName ??
    stableName;

  // The vehicle the sim ran this shift with — prefer an explicit vehicle name,
  // fall back to the shift name (often the rego in ELC feeds).
  const finalVehicleName =
    (vehicleName && !isGenericShiftName(vehicleName, shiftId) ? vehicleName : null) ??
    (shiftName && !isGenericShiftName(shiftName, shiftId) ? shiftName : null);

  return {
    stableName,
    fleetName: finalFleetName,
    manifestName: finalManifestName,
    shiftBand,
    vehicleName: finalVehicleName,
  };
}

function minutesBetweenActivities(a: NormalizedActivity, b: NormalizedActivity): number {
  const byTimestamp = minutesBetween(a._timeMs, b._timeMs);
  if (byTimestamp !== DEFAULT_SEGMENT_MINUTES) return byTimestamp;
  if (a._clockMinutes != null && b._clockMinutes != null) {
    let diff = b._clockMinutes - a._clockMinutes;
    if (diff < 0) diff += 24 * 60;
    if (diff > 0) return Math.max(1, Math.round(diff));
  }
  return DEFAULT_SEGMENT_MINUTES;
}

function generateStopsHashLikeRouteConverter(routeStops: FixedRouteStop[]): string {
  return routeStops
    .map((stop) => `${stop.id}:${stop.coordinates?.lat || 0}:${stop.coordinates?.lng || 0}`)
    .join('|');
}

function computeRouteGeometryHashLikeConverter(routeStops: FixedRouteStop[], isCirculator: boolean, isBusRoute: boolean): string {
  const stopsHash = generateStopsHashLikeRouteConverter(routeStops);
  return `${stopsHash}::c=${isCirculator ? 1 : 0}::b=${isBusRoute ? 1 : 0}`;
}

function mergeSegmentGeometriesLatLng(segments: [number, number][][]): [number, number][] {
  let coordinates: [number, number][] = [];
  for (const segment of segments) {
    if (!Array.isArray(segment) || segment.length === 0) continue;
    if (coordinates.length === 0) {
      coordinates = [...segment];
      continue;
    }
    const last = coordinates[coordinates.length - 1];
    const first = segment[0];
    const samePoint = Math.abs(last[0] - first[0]) < 1e-6 && Math.abs(last[1] - first[1]) < 1e-6;
    coordinates = samePoint ? [...coordinates, ...segment.slice(1)] : [...coordinates, ...segment];
  }
  return coordinates;
}

export function transformSimulationResultsToFixedRoutes(
  input: SimulationResultsToFixedRoutesInput
): SimulationResultsToFixedRoutesResult {
  const warnings: string[] = [];
  const rows: NormalizedActivity[] = [];

  input.activities.forEach((activity, index) => {
    const coords = resolveLatLng(activity);
    if (!coords) return;
    rows.push({
      ...activity,
      _shiftId: shiftIdFor(activity),
      _timeMs: resolveTimeMs(activity),
      _clockMinutes: resolveClockMinutes(activity),
      _seq: resolveSequence(activity, index),
      _lat: coords[0],
      _lng: coords[1],
    });
  });

  const byShift = new Map<string, NormalizedActivity[]>();
  rows.forEach((row) => {
    const existing = byShift.get(row._shiftId) ?? [];
    existing.push(row);
    byShift.set(row._shiftId, existing);
  });

  const allStops: FixedRouteStop[] = [];
  const allRoutes: FixedRoute[] = [];

  Array.from(byShift.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([shiftId, activities], routeIndex) => {
      const ordered = [...activities].sort((a, b) => {
        if (a._seq !== b._seq) return a._seq - b._seq;
        if (a._timeMs !== b._timeMs) return a._timeMs - b._timeMs;
        return 0;
      });

      const stopConfigs: FixedRouteStopConfig[] = [];
      const orderedStopIds: string[] = [];
      const orderedActivitiesByStopId = new Map<string, NormalizedActivity>();

      const routeStopsForHash: FixedRouteStop[] = [];
      ordered.forEach((act, idx) => {
        const agg = buildStopAgg(act);
        const stopId = `sim-stop-${input.runId}-${shiftId}-${idx + 1}-${act._lat.toFixed(6)}-${act._lng.toFixed(6)}`;
        const pd = inferPickDrop(act);
        // Stop label resolution. The consolidated bus-stop / hub name rides
        // through the echoed `trips[].ride.from_address` / `to_address`
        // (Postcode Stop Assignment writes it there), so it survives reopen /
        // reload with no server-side change. `external_stop_reference_id` is kept
        // as the top preference for forward-compat but is never echoed today.
        //   PostcodeStopAssignmentPage → ride from_address/to_address = stop name
        //   → HTTM persists + echoes them on /activities trips[].ride
        //   → resolveStopName picks the leg-appropriate one here
        //   → FixedRouteStop.name (and externalStopReferenceId when a real ref exists)
        //   → fixedRouteDataExporter.transformStopForPayload uses it as the label
        const resolvedStopName = resolveStopName(act);
        const externalStopReferenceId = nonEmptyString((act as Record<string, unknown>).external_stop_reference_id);
        const stopName = resolvedStopName ?? `Stop ${idx + 1}`;
        const builtStop: FixedRouteStop = {
          id: stopId,
          name: stopName,
          coordinates: { lat: act._lat, lng: act._lng },
          address: typeof (act as Record<string, unknown>).address === 'string' ? String((act as Record<string, unknown>).address) : undefined,
          externalStopReferenceId,
          simulationImport: {
            runId: input.runId,
            shiftId,
            stopSequence: idx + 1,
            pickupCount: pd === 'pick' ? agg.pickupCount : 0,
            dropoffCount: pd === 'drop' ? agg.dropoffCount : 0,
            pickedUpRiderIds: pd === 'pick' ? Array.from(agg.pickedUpRiderIds) : [],
            droppedOffRiderIds: pd === 'drop' ? Array.from(agg.droppedOffRiderIds) : [],
          },
        };
        allStops.push(builtStop);
        routeStopsForHash.push(builtStop);
        stopConfigs.push({ stopId, waitTimeMinutes: 1 });
        orderedStopIds.push(stopId);
        orderedActivitiesByStopId.set(stopId, act);
      });

      if (orderedStopIds.length < 2) {
        warnings.push(`Skipped shift ${shiftId}: fewer than 2 distinct stops.`);
        return;
      }

      const shiftStartMs = ordered
        .map((a) => a._timeMs)
        .filter((t) => Number.isFinite(t))
        .reduce((min, t) => Math.min(min, t), Number.POSITIVE_INFINITY);
      const shiftEndMs = ordered
        .map((a) => a._timeMs)
        .filter((t) => Number.isFinite(t))
        .reduce((max, t) => Math.max(max, t), Number.NEGATIVE_INFINITY);

      const segments: FixedRouteSegment[] = [];
      for (let i = 0; i < orderedStopIds.length - 1; i++) {
        const fromId = orderedStopIds[i];
        const toId = orderedStopIds[i + 1];
        const fromAct = orderedActivitiesByStopId.get(fromId);
        const toAct = orderedActivitiesByStopId.get(toId);
        segments.push({
          fromStopId: fromId,
          toStopId: toId,
          travelTimeMinutes: fromAct && toAct ? minutesBetweenActivities(fromAct, toAct) : DEFAULT_SEGMENT_MINUTES,
        });
      }

      const clockMinutes = ordered
        .map((a) => a._clockMinutes)
        .filter((t): t is number => t != null && Number.isFinite(t));
      const computedLoopMinutes =
        segments.reduce((sum, seg) => sum + seg.travelTimeMinutes, 0) +
        stopConfigs.reduce((sum, stop) => sum + stop.waitTimeMinutes, 0);
      const startTime = clockMinutes.length > 0
        ? minutesToHHmm(clockMinutes[0])
        : Number.isFinite(shiftStartMs)
          ? minutesToHHmm(new Date(shiftStartMs).getUTCHours() * 60 + new Date(shiftStartMs).getUTCMinutes())
          : '06:00';
      const endTimeFromData = clockMinutes.length > 0
        ? minutesToHHmm(clockMinutes[clockMinutes.length - 1])
        : Number.isFinite(shiftEndMs)
          ? minutesToHHmm(new Date(shiftEndMs).getUTCHours() * 60 + new Date(shiftEndMs).getUTCMinutes())
          : null;
      const endTime = endTimeFromData ?? addMinutesToHHmm(startTime, Math.max(1, computedLoopMinutes));

      const seededShiftSegments = input.routeGeometryByShift?.[shiftId] ?? [];
      const expectedSegmentCount = Math.max(orderedStopIds.length - 1, 0);
      let seededGeometry: FixedRoute['geometry'] | undefined;
      if (seededShiftSegments.length > 0) {
        if (seededShiftSegments.length !== expectedSegmentCount) {
          warnings.push(
            `Shift ${shiftId}: geometry segment count mismatch (expected ${expectedSegmentCount}, got ${seededShiftSegments.length}).`
          );
        }
        const normalizedSegments = seededShiftSegments
          .filter((segment) => Array.isArray(segment) && segment.length > 1)
          .map((segment) => segment.map(([lat, lng]) => [lat, lng] as [number, number]));
        const mergedCoordinates = mergeSegmentGeometriesLatLng(normalizedSegments);
        if (mergedCoordinates.length > 1) {
          seededGeometry = {
            coordinates: mergedCoordinates,
            calculatedAt: Date.now(),
            stopsHash: computeRouteGeometryHashLikeConverter(routeStopsForHash, false, true),
            segmentGeometries: normalizedSegments,
          };
        } else {
          warnings.push(`Shift ${shiftId}: path geometry was present but unusable after normalization.`);
        }
      } else {
        warnings.push(`Shift ${shiftId}: no path geometry found in /paths; route will use routing fallback.`);
      }

      const naming = resolveShiftNaming(shiftId, ordered, input.routeMetadataByShift?.[shiftId]);
      allRoutes.push({
        id: `sim-route-${input.runId}-${shiftId}`,
        name: naming.stableName ?? `Shift ${routeIndex + 1} (${shiftId.slice(0, 8)})`,
        color: colorForImportedRoute(routeIndex),
        startTime,
        endTime,
        stops: stopConfigs,
        segments,
        isBusRoute: true,
        geometry: seededGeometry,
        simulationImport: {
          runId: input.runId,
          shiftId,
          source: 'simulation_results',
          networkTag: 'optimised_fixed_route_network',
          networkLayerName: OPTIMISED_FIXED_ROUTE_LAYER_NAME,
          shiftBand: naming.shiftBand ?? undefined,
          stableName: naming.stableName ?? undefined,
          fleetName: naming.fleetName ?? undefined,
          manifestName: naming.manifestName ?? undefined,
          // The route's own vehicle (sim rego/name) — matched against the Ops
          // vehicle list when attaching operations.
          opsVehicleLabel: naming.vehicleName ?? naming.fleetName ?? undefined,
        },
      });
    });

  return {
    stops: allStops,
    routes: allRoutes,
    warnings,
  };
}
